import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthz } from '@/features/auth/hooks/useAuthz'
import LoadingSpinner from '@/shared/components/ui/LoadingSpinner'
import type { UserPermissions } from '@/features/auth/types'

interface Props {
  permission: keyof UserPermissions
  children: ReactNode
}

/**
 * Fail-closed route guard.
 *
 * An unresolved or errored authorization lookup is treated as a denial, never a
 * bypass. Redirects to the dashboard rather than /login: the user is
 * authenticated, they simply lack this permission.
 */
export default function RequirePermissionRoute({ permission, children }: Props) {
  const { can, isLoading, isError } = useAuthz()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isError || !can(permission)) {
    return <Navigate to="/" replace state={{ denied: location.pathname }} />
  }

  return <>{children}</>
}
